import {
  HttpMethod,
  HeadersType,
  Milliseconds,
  Header,
  IExternalRequester,
  SendRequestParametersType,
  Parser,
  RequestType,
  KameleoonResponseType,
  KameleoonUtils,
  KameleoonLogger,
  IUrlProvider,
} from '@kameleoon/javascript-sdk-core';
import { RequestParametersType, CredentialsType } from './types';

// --- Note ---
// `RequesterMiddleware` is a decorator for `Requester` class to add
// authorization layer to Node.js SDK
export class RequesterMiddleware implements IExternalRequester {
  private readonly requester: IExternalRequester;
  private readonly urlProvider: IUrlProvider;
  private readonly credentials: CredentialsType;
  private readonly base64Credentials: string;
  private nextAccessTokenUpdate?: number | null;
  private accessToken?: string | null;
  private readonly _isKameleoonRequester: boolean = false;

  constructor(
    requester: IExternalRequester,
    credentials: CredentialsType,
    urlProvider: IUrlProvider,
  ) {
    this.requester = requester;
    this.urlProvider = urlProvider;
    this.credentials = credentials;
    this.base64Credentials = this.encodeBase64(
      `${credentials.clientId}:${credentials.clientSecret}`,
    );

    this._isKameleoonRequester = 'isKameleoonRequester' in requester;
  }

  get isKameleoonRequester(): boolean {
    return this._isKameleoonRequester;
  }

  public async sendRequest<T extends RequestType>({
    url,
    parameters,
    retryCount,
    requestType,
  }: SendRequestParametersType<T>): Promise<KameleoonResponseType> {
    let headers: HeadersType | undefined;
    let updatedParameters = parameters;
    const needAuth =
      requestType === RequestType.Tracking ||
      requestType === RequestType.RemoteData;

    if (needAuth) {
      let hasGoodToken = !this.shouldUpdateAccessToken();

      if (!hasGoodToken) {
        hasGoodToken = await this.fetchAccessToken(parameters.signal);
      }

      if (hasGoodToken) {
        headers = this.getAuthHeader(parameters);
        updatedParameters = { ...parameters, headers };
      }
    }

    if (requestType === RequestType.Tracking) {
      let response = await this.requester.sendRequest({
        url,
        parameters: updatedParameters,
        retryCount,
        requestType,
      });

      if (response.status === 401) {
        // --- Note ---
        // Auth error - retry with new token
        this.nextAccessTokenUpdate = null;
        this.accessToken = null;

        // --- Note ---
        // If this is the last retry with `401` status
        // we try once more without authorization
        if (retryCount === 2) {
          response = await this.requester.sendRequest({
            url,
            parameters,
            requestType,
            retryCount,
          });
        }

        return response;
      }

      // --- Note ---
      // Forbidden (bot filtering) - simulate successful response from utils
      // to indicate that the bot was filtered
      if (response.status === 403) {
        return KameleoonUtils.simulateSuccessRequest<RequestType.Tracking>(
          requestType,
          null,
        );
      }

      return response;
    }

    return await this.requester.sendRequest({
      url,
      parameters: updatedParameters,
      requestType,
      retryCount,
    });
  }

  private async fetchAccessToken(signal: AbortSignal | null): Promise<boolean> {
    const headers = {
      [Header.Authorization]: `Basic ${this.base64Credentials}`,
      [Header.ContentType]: 'application/x-www-form-urlencoded',
    };
    const url = this.urlProvider.getAccessTokenUrl();

    KameleoonLogger.debug(() => {
      return `Running access token request Method: ${
        HttpMethod.Post
      }, Url: ${url}, with authorization token: ${Parser.parseSecret(
        this.base64Credentials,
      )}`;
    });

    const parameters = {
      method: HttpMethod.Post,
      signal,
      headers,
      body: `grant_type=client_credentials`,
    } as const;

    const response = await this.requester.sendRequest({
      url,
      requestType: RequestType.Tracking,
      parameters,
    });

    KameleoonLogger.debug(() => {
      return `Received access token response: status: ${
        response.status
      } for request Method: ${
        HttpMethod.Post
      }, Url: ${url}, with authorization token: ${Parser.parseSecret(
        this.base64Credentials,
      )}`;
    });
    if (response.ok) {
      const json = await response.json();

      if (typeof json?.access_token === 'string') {
        this.accessToken = json.access_token;

        this.nextAccessTokenUpdate = Date.now() + 1.8 * Milliseconds.Hour;
      }

      return true;
    }

    return false;
  }

  private shouldUpdateAccessToken(): boolean {
    if (!this.nextAccessTokenUpdate) {
      return true;
    }

    return Date.now() > this.nextAccessTokenUpdate;
  }

  private getAuthHeader(
    parameters: RequestParametersType,
  ): HeadersType | undefined {
    if (!('headers' in parameters)) {
      return { [Header.Authorization]: `Bearer ${this.accessToken}` };
    }

    const headers = parameters.headers as HeadersType;

    if (!this.accessToken) {
      return headers;
    }

    return {
      ...headers,
      [Header.Authorization]: `Bearer ${this.accessToken}`,
    };
  }

  private encodeBase64(value: string): string {
    if (typeof Buffer !== 'undefined') {
      return Buffer.from(value).toString('base64');
    }

    const bytes = new TextEncoder().encode(value);
    return btoa(String.fromCodePoint(...bytes));
  }
}
